<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'primevue/button';

import { useSession } from '@/shell/stores/session';
import { useAuctionEvent } from '@/shell/stores/auctionEvent';
import PaddleIllustration from './illustrations/PaddleIllustration.vue';

const session = useSession();
const route = useRoute();
const router = useRouter();
const auctionEvent = useAuctionEvent();

const slug = computed(
  () => (route.params.slug as string | undefined) ?? auctionEvent.slug ?? '',
);

// Only show on browse-y routes (catalog, item detail, wishlist). Hide on
// register, sign-in, my-bids, wins, checkout, the gate page itself — those
// either ARE the registration path or don't make sense to nudge from.
const allowedSuffixes = ['/items', '/wishlist'];
const onItemDetail = computed(() =>
  /\/items\/\d+$/.test(route.path),
);
const onAllowedRoute = computed(
  () => allowedSuffixes.some((s) => route.path.endsWith(s)) || onItemDetail.value,
);

const visible = computed(
  () => session.bidder == null && onAllowedRoute.value && !!slug.value,
);

function onSignIn(): void {
  router.push({
    path: `/${slug.value}/sign-in`,
    query: { next: route.fullPath },
  });
}
</script>

<template>
  <div
    v-if="visible"
    class="register-banner"
    data-testid="register-banner"
  >
    <div class="register-banner__illo">
      <PaddleIllustration />
    </div>
    <div class="register-banner__copy">
      <span class="register-banner__title">New here? Register to bid</span>
    </div>
    <Button
      label="Register"
      class="register-banner__cta"
      data-testid="register-banner__cta"
      @click="onSignIn"
    />
  </div>
</template>

<style scoped>
.register-banner {
  display: flex;
  align-items: center;
  gap: var(--app-space-12);
  padding: var(--app-space-8) var(--app-space-12);
  margin: var(--app-space-4) auto var(--app-space-8);
  /* On mobile, sit 20px in from each edge; on wider screens, cap at 480px
     and center. The min() keeps it from touching the content-well edges
     either way. */
  max-width: min(480px, calc(100% - var(--app-space-40)));
  background: linear-gradient(135deg, #FFF6E5 0%, #FFEDD3 100%);
  border: var(--app-border-width-default) solid #F2D8A6;
  border-radius: var(--app-radius-lg);
  box-shadow: 0 1px 2px rgba(178, 109, 31, 0.08);
}
.register-banner__illo {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
}
.register-banner__copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}
.register-banner__title {
  font-size: var(--app-font-size-body-sm);
  line-height: var(--app-line-height-body-sm);
  font-weight: var(--app-font-weight-semibold);
  color: var(--app-text-primary);
}
.register-banner__sub {
  font-size: var(--app-font-size-caption);
  line-height: var(--app-line-height-caption);
  color: var(--app-text-muted);
}
.register-banner__cta {
  flex-shrink: 0;
}

@media (max-width: 380px) {
  .register-banner__sub {
    display: none;
  }
}

/* At tablet+ the banner sits in a flex row next to the search bar
   (App.vue's .shell-top-row), so drop the centering margin and the
   width cap — the parent flex layout governs sizing. */
@media (min-width: 641px) {
  .register-banner {
    margin: 0;
    max-width: none;
  }
}
</style>
